import { useEffect } from 'preact/hooks';
import type { Slot, Stats } from '../../../data/schema';
import { loadContent } from '../../../data/content';
import type { Snapshot } from '../../../core/run';
import { hudState } from '../state';
import { changeText } from '../derivedText';

const content = loadContent();
const SLOTS: Slot[] = ['arme', 'coiffe', 'cape', 'amulette', 'anneau', 'bottes'];
const SLOT_LABEL: Record<Slot, string> = { arme: 'Arme', coiffe: 'Coiffe', cape: 'Cape', amulette: 'Amulette', anneau: 'Anneau', bottes: 'Bottes' };
const STAT_LABEL: Record<keyof Stats, string> = { vitalite: 'Vitalité', force: 'Force', intelligence: 'Intelligence', agilite: 'Agilité', chance: 'Chance', sagesse: 'Sagesse' };

function statLines(stats: Partial<Stats>): string[] {
  return (Object.keys(STAT_LABEL) as (keyof Stats)[]).filter((k) => stats[k]).map((k) => `+${stats[k]} ${STAT_LABEL[k]}`);
}

function activeSetBonuses(equipment: Partial<Record<Slot, string>>): { key: string; name: string; count: number; lines: string[] }[] {
  const worn = Object.values(equipment).filter((id): id is string => !!id);
  const out: { key: string; name: string; count: number; lines: string[] }[] = [];
  for (const set of Object.values(content.sets)) {
    const n = worn.filter((id) => set.items.includes(id)).length;
    for (const b of set.bonuses) if (b.count <= n) out.push({ key: `${set.id}-${b.count}`, name: set.name, count: b.count, lines: statLines(b.stats) });
  }
  return out;
}

export function Shop({ shop, kamas, equipment }: { shop: NonNullable<Snapshot['shop']>; kamas: number; equipment: Partial<Record<Slot, string>> }) {
  const bonuses = activeSetBonuses(equipment);
  const free = shop.freeRerolls > 0;
  // pas de raccourci chiffré ici (achats involontaires) : seulement Échap pour quitter
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.code === 'Escape') hudState.send({ type: 'leaveShop' }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div class="panel shop-panel">
      <header class="shop-head"><h2>Marchand</h2><span class="shop-kamas"><img src="/assets/icons/ui/kamas.png" alt="" /> {kamas} Kamas</span></header>
      <div class="shop-scroll">
      <div class="shop-cols">
        <section class="shop-offers" aria-label="Offres du marchand">
          {shop.offers.map((o, i) => (o === null ? (
            <div key={i} class="shop-offer sold"><span>Vendu</span></div>
          ) : (
            <div key={`${i}-${o.item.id}`} class="shop-offer">
              <img src={`/assets/${o.item.icon}.png`} alt="" />
              <div class="shop-info">
                <b>{o.item.name}</b>
                <small>{SLOT_LABEL[o.item.slot]}{o.item.set ? ` · ${content.sets[o.item.set]?.name ?? ''}` : ''}</small>
                <small class="stats">{statLines(o.item.stats).join(', ')}</small>
                {(shop.effects[i]?.length ?? 0) > 0 && <small class="shop-effect">{shop.effects[i]!.map(changeText).join(', ')}</small>}
              </div>
              <div class="shop-buy">
                <span class="price">{o.price} K</span>
                <button onMouseDown={(e) => e.preventDefault()} aria-label={`Acheter ${o.item.name} pour ${o.price} Kamas`} disabled={kamas < o.price} onClick={() => hudState.send({ type: 'buy', index: i })}>Acheter</button>
              </div>
            </div>
          )))}
        </section>
        <section class="shop-equipment" aria-label="Équipement">
          <h3>Équipement</h3>
          <div class="equipment-grid">
            {SLOTS.map((slot) => {
              const id = equipment[slot];
              const item = id ? content.items[id] : undefined;
              return item
                ? <div key={slot} class="equip-slot" title={`${item.name} : ${statLines(item.stats).join(', ')}`}><img src={`/assets/${item.icon}.png`} alt={item.name} /><small>{SLOT_LABEL[slot]}</small></div>
                : <div key={slot} class="equip-slot empty"><span>{SLOT_LABEL[slot]}</span></div>;
            })}
          </div>
          <h3>Bonus de panoplie</h3>
          {bonuses.length === 0 ? <p class="muted">Aucun bonus actif.</p> : (
            <ul class="set-bonuses">{bonuses.map((b) => <li key={b.key}><b>{b.name}</b> ({b.count} pièces) : {b.lines.join(', ')}</li>)}</ul>
          )}
        </section>
      </div>
      </div>
      {/* pied hors de la zone défilante : Quitter reste visible même quand les offres dépassent (téléphone en paysage) */}
      <div class="shop-footer shop-actions">
        <button onMouseDown={(e) => e.preventDefault()} aria-label={free ? 'Relancer le marchand gratuitement' : `Relancer le marchand pour ${shop.rerollPrice} Kamas`} disabled={!free && kamas < shop.rerollPrice} onClick={() => hudState.send({ type: 'rerollShop' })}>{free ? 'Relancer (gratuit)' : `Relancer (${shop.rerollPrice} K)`}</button>
        <button class="leave" onMouseDown={(e) => e.preventDefault()} aria-label="Quitter le marchand" onClick={() => hudState.send({ type: 'leaveShop' })}>Quitter</button>
      </div>
    </div>
  );
}
